import { DndContext } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { CornerDownRight, PercentIcon } from "lucide-react";
import React, { useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import TaskBdCustomField from "./TaskBdCustomField";

import FormInput from "@/components/form-fields/form-input";
import { DragableObject } from "@/components/reusables/Dnd/DragableObject";
import { FormSelectSingle } from "@/components/reusables/custom-form-fields/FormSelect";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { HUNDRED } from "@/constants/constants";
import { ITEM_STATUS } from "@/constants/items.constant";
import { cn } from "@/lib/utils";
import { useGetItemsQuery } from "@/redux/api/itemQuery";
import { ITaskBreakDownSchema } from "@/schemas/task-and-claims";
import { InputKeyLimiter } from "@/utils/helper-functions/form.helper";

type TTaskBdField = {
  sectionId: number;
  isHideItem?: boolean;
};

const PercentageChange = ({
  itemId,
  sectionId,
}: {
  itemId: number;
  sectionId: number;
}): React.JSX.Element => {
  const form = useFormContext<ITaskBreakDownSchema>();

  const singleData = form.watch(`data.${sectionId}.details.${itemId}`);
  const customItems = form.getValues(
    `data.${sectionId}.details.${itemId}.customDetails`,
  );

  const changeCalculator = (): void => {
    const { newProgress, lastTaskProgress } = singleData;

    const data = form.getValues(`data.${sectionId}.details`);

    if (data[itemId]?.isCustom && customItems.length > 0) {
      const customItemsLength = customItems?.length;

      const totalPercentage = customItems?.reduce((acc: number, item) => {
        return Number(item?.newProgress) + acc;
      }, 0);
      form.setValue(
        `data.${sectionId}.details.${itemId}.newProgress`,
        Number((totalPercentage / customItemsLength)?.toFixed(2)),
        {
          shouldDirty: true,
        },
      );
    }
    const newChange = Number((newProgress - lastTaskProgress).toFixed(2));

    form.setValue(`data.${sectionId}.details.${itemId}.change`, newChange, {
      shouldDirty: true,
    });
  };

  useEffect(() => {
    if (typeof itemId === "number" && singleData) {
      changeCalculator();
    }
  }, [JSON.stringify(singleData)]);

  return (
    <FormInput
      form={form}
      name={`data.${sectionId}.details.${itemId}.change`}
      className='w-full'
      input={{
        disabled: true,
        placeholder: `Change`,

        type: "number",
        step: 0.01,
        required: true,
      }}
    />
  );
};

const TaskBdField = ({
  sectionId,
  isHideItem = false,
}: TTaskBdField): React.JSX.Element => {
  const form = useFormContext<ITaskBreakDownSchema>();
  const mainArray = useFieldArray({
    control: form.control,
    name: `data.${sectionId}.details`,
    keyName: "detailsId",
  });

  const { fields: ItemFields } = mainArray;

  const { data: itemList } = useGetItemsQuery({
    filter: "?type=ESTIMATION",
  });

  return (
    <>
      <DndContext>
        <SortableContext items={ItemFields.map((field) => field?.detailsId)}>
          {ItemFields?.sort((a, b) => a.position - b.position)?.map(
            (field, ItemId) => {
              return (
                <DragableObject
                  key={`dataName-${field.detailsId}`}
                  id={field.detailsId}
                  isCenter={!field.isCustom}
                >
                  <Collapsible className='w-full' defaultOpen>
                    <div>
                      <div
                        className={cn(
                          `group grid w-full  rounded-md gap-4 items-start`,
                          isHideItem
                            ? "grid-cols-[2.2fr_2.5fr_1fr_1fr_1fr_1fr_1.1fr_0.4fr]"
                            : "grid-cols-[2fr_2.5fr_1fr_1fr_1fr_1fr_1.1fr_0.4fr]",
                        )}
                      >
                        {!isHideItem && (
                          <div className=''>
                            {field?.isCustom ? (
                              <FormInput
                                form={form}
                                name={`data.${sectionId}.details.${ItemId}.item`}
                                className='w-full'
                                input={{
                                  disabled: true,
                                  placeholder: `Item Name`,
                                  type: "text",
                                  required: true,
                                }}
                              />
                            ) : (
                              <FormInput
                                form={form}
                                name={`data.${sectionId}.details.${ItemId}.item`}
                                className='w-full'
                                render={(field) => (
                                  <FormSelectSingle
                                    placeholder='Please Select Item'
                                    field={field}
                                    className='w-full '
                                    disabled
                                    options={
                                      itemList?.data
                                        ?.filter(
                                          (data) =>
                                            data?.status === ITEM_STATUS.ACTIVE,
                                        )
                                        .map((item) => ({
                                          label: item.title || "",
                                          value: item.id,
                                        })) || []
                                    }
                                    onFieldValueChange={(value) => {
                                      const item = itemList?.data?.find(
                                        (item) => item.id === value,
                                      );
                                      form.setValue(
                                        `data.${sectionId}.details.${ItemId}.item`,
                                        item?.id || "",
                                        {
                                          shouldDirty: true,
                                        },
                                      );
                                      form.setValue(
                                        `data.${sectionId}.details.${ItemId}.description`,
                                        item?.description || "",
                                        {
                                          shouldDirty: true,
                                        },
                                      );
                                      form.setValue(
                                        `data.${sectionId}.details.${ItemId}.unit`,
                                        item?.unit || "unit",
                                        {
                                          shouldDirty: true,
                                        },
                                      );
                                    }}
                                  />
                                )}
                              />
                            )}
                          </div>
                        )}

                        <FormInput
                          form={form}
                          name={`data.${sectionId}.details.${ItemId}.description`}
                          className='w-full'
                          input={{
                            disabled: true,
                            placeholder: `Description`,
                            type: "text",
                          }}
                        />

                        <FormInput
                          form={form}
                          name={`data.${sectionId}.details.${ItemId}.unit`}
                          className='w-full'
                          input={{
                            disabled: true,
                            placeholder: `Unit`,
                            type: "text",
                          }}
                        />

                        <FormInput
                          form={form}
                          name={`data.${sectionId}.details.${ItemId}.quantity`}
                          className='w-full'
                          input={{
                            disabled: true,
                            placeholder: `Quantity`,
                            type: "number",
                          }}
                        />

                        <FormInput
                          form={form}
                          name={`data.${sectionId}.details.${ItemId}.lastTaskProgress`}
                          render={(field) => (
                            <Input
                              disabled
                              placeholder={`Last Progress`}
                              className='w-full'
                              type='number'
                              icon={{
                                icon: PercentIcon,
                                position: "right",
                              }}
                              step={0.01}
                              {...field}
                            />
                          )}
                        />

                        <FormInput
                          form={form}
                          name={`data.${sectionId}.details.${ItemId}.newProgress`}
                          render={(field) => (
                            <Input
                              className='w-full border-primary'
                              disabled={
                                ItemFields[ItemId].isCustom &&
                                ItemFields[ItemId].customDetails?.length > 0
                              }
                              icon={{
                                icon: PercentIcon,
                                position: "right",
                              }}
                              step={0.01}
                              onKeyDown={(e) =>
                                InputKeyLimiter({ event: e, maxValue: HUNDRED })
                              }
                              {...field}
                            />
                          )}
                        />

                        <PercentageChange
                          itemId={ItemId}
                          sectionId={sectionId}
                        />

                        <div className='flex m-auto'>
                          {field?.isCustom ? (
                            <CollapsibleTrigger>
                              <CornerDownRight
                                className='w-5 ml-2 cursor-pointer'
                                color='blue'
                              />
                            </CollapsibleTrigger>
                          ) : (
                            <div className='w-5 ml-2' />
                          )}
                        </div>
                      </div>
                      {field?.isCustom && (
                        <CollapsibleContent>
                          <TaskBdCustomField
                            sectionId={sectionId}
                            ItemId={ItemId}
                          />
                        </CollapsibleContent>
                      )}
                    </div>
                  </Collapsible>
                </DragableObject>
              );
            },
          )}
        </SortableContext>
      </DndContext>
    </>
  );
};

export default TaskBdField;
